import React from 'react';
import { Popover, PopoverTrigger, PopoverContent, Flex, Text, Divider, LoadingOrbit, Heading, Box, Markdown, useToast } from '@chatsdet/ui';
import { Warning, CopyIcon, CheckCircle } from '@phosphor-icons/react';
import { useCredentialSecret } from '../../../hooks/useCredentialSecret';

type CredentialSecretPopoverProps = {
  propertyId: string | null;
  credentialId: string | null;
  label: string;
  canViewSecrets: boolean;
  isRestricted: boolean;
};

const FieldRow = ({
  label,
  value,
  onCopy,
  copied,
}: {
  label: string;
  value: React.ReactNode;
  onCopy?: () => void;
  copied?: boolean;
}) => (
  <Flex direction="column" gap="xs">
    <Flex align="center" gap="xs">
      <Text size="xs" className="text-muted-foreground uppercase tracking-wide">
        {label}
      </Text>
      {onCopy ? (
        <button
          type="button"
          className="inline-flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring transition-colors"
          onClick={onCopy}
          aria-label={`Copy ${label}`}
        >
          {copied ? <CheckCircle size={16} className="text-emerald-500" /> : <CopyIcon size={14} />}
        </button>
      ) : null}
    </Flex>
    <Text size="sm" className="text-foreground break-words">
      {value}
    </Text>
  </Flex>
);

export function CredentialSecretPopover({
  propertyId,
  credentialId,
  label,
  canViewSecrets,
  isRestricted,
}: CredentialSecretPopoverProps) {
  const isInteractive = Boolean(propertyId && credentialId && canViewSecrets && !isRestricted);
  const [isOpen, setIsOpen] = React.useState(false);
  const [copiedField, setCopiedField] = React.useState<'username' | 'password' | null>(null);
  const copyResetTimeout = React.useRef<number | null>(null);
  const { toast } = useToast();

  React.useEffect(() => {
    setIsOpen(false);
  }, [propertyId, credentialId]);

  React.useEffect(() => {
    return () => {
      if (copyResetTimeout.current) {
        window.clearTimeout(copyResetTimeout.current);
        copyResetTimeout.current = null;
      }
    };
  }, []);

  const handleCopy = React.useCallback(
    (value: string, field: 'username' | 'password') => {
      navigator.clipboard
        .writeText(value)
        .then(() => {
          toast.success(`${field === 'username' ? 'Username' : 'Password'} copied`);
          setCopiedField(field);
          if (copyResetTimeout.current) {
            window.clearTimeout(copyResetTimeout.current);
          }
          copyResetTimeout.current = window.setTimeout(() => {
            setCopiedField(null);
          }, 3000);
        })
        .catch(() => {
          toast.error('Failed to copy to clipboard');
        });
    },
    [toast]
  );

  const credentialQuery = useCredentialSecret(propertyId, credentialId, {
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
  });

  if (!isInteractive) {
    return (
      <Text size="sm" className="text-muted-foreground">
        {label}
      </Text>
    );
  }

  const credential = credentialQuery.data?.credential ?? null;
  const data = credential?.data ?? {};
  const username =
    typeof (data.username as string | undefined) === 'string'
      ? (data.username as string)
      : typeof (data.email as string | undefined) === 'string'
        ? (data.email as string)
        : typeof (data.user as string | undefined) === 'string'
          ? (data.user as string)
          : null;
  const password =
    typeof (data.password as string | undefined) === 'string' ? (data.password as string) : null;

  return (
    <Popover isOpen={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger>
        <Box
          role="button"
          tabIndex={0}
          className="text-sm font-normal text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring/60 rounded cursor-pointer"
          onClick={() => setIsOpen((prev) => !prev)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setIsOpen((prev) => !prev);
            }
          }}
        >
          {label}
        </Box>
      </PopoverTrigger>
      <PopoverContent className="w-80 space-y-4">
        <div className="space-y-1">
          <Heading level={4} className="text-base">
            Credential details
          </Heading>
          <Text size="sm" className="text-muted-foreground">
            Securely decrypted when opened
          </Text>
        </div>
        <Divider />
        {credentialQuery.isLoading ? (
          <Flex align="center" justify="center" className="py-6">
            <LoadingOrbit size="sm" />
          </Flex>
        ) : credentialQuery.isError ? (
          <Flex direction="column" align="center" gap="xs" className="text-center py-4">
            <Warning size={24} className="text-destructive" />
            <Text size="sm" className="text-muted-foreground">
              Failed to load credential.
            </Text>
          </Flex>
        ) : credentialQuery.data?.accessDenied ? (
          <Flex direction="column" align="center" gap="xs" className="text-center py-4">
            <Warning size={24} className="text-amber-500" />
            <Text size="sm" className="text-muted-foreground">
              You don’t have permission to view this credential.
            </Text>
          </Flex>
        ) : credentialQuery.data?.notFound ? (
          <Flex direction="column" align="center" gap="xs" className="text-center py-4">
            <Warning size={24} className="text-muted-foreground" />
            <Text size="sm" className="text-muted-foreground">
              Credential not found.
            </Text>
          </Flex>
        ) : credential ? (
          <Flex direction="column" gap="md">
            <FieldRow label="Name" value={credential.name} />
            {username ? (
              <FieldRow
                label="Username"
                value={username}
                onCopy={() => handleCopy(username, 'username')}
                copied={copiedField === 'username'}
              />
            ) : null}
            {password ? (
              <FieldRow
                label="Password"
                value={password}
                onCopy={() => handleCopy(password, 'password')}
                copied={copiedField === 'password'}
              />
            ) : null}
            {credential.loginUrl ? (
              <FieldRow
                label="Login URL"
                value={
                  <a
                    href={credential.loginUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm text-primary underline break-all"
                  >
                    {credential.loginUrl}
                  </a>
                }
              />
            ) : null}
            {credential.loginInstructions ? (
              <Box className="space-y-2">
                <Text size="xs" className="text-muted-foreground uppercase tracking-wide">
                  Instructions
                </Text>
                <Markdown className="prose prose-sm max-w-none text-foreground prose-p:text-foreground prose-li:text-foreground/90">
                  {credential.loginInstructions}
                </Markdown>
              </Box>
            ) : null}
            {credential.sessionVerificationGuidance ? (
              <Box className="space-y-2">
                <Text size="xs" className="text-muted-foreground uppercase tracking-wide">
                  Session verification guidance
                </Text>
                <Markdown className="prose prose-sm max-w-none text-foreground prose-p:text-foreground prose-li:text-foreground/90">
                  {credential.sessionVerificationGuidance}
                </Markdown>
              </Box>
            ) : null}
          </Flex>
        ) : (
          <Flex direction="column" align="center" gap="xs" className="text-center py-4">
            <Warning size={24} className="text-muted-foreground" />
            <Text size="sm" className="text-muted-foreground">
              No credential details available.
            </Text>
          </Flex>
        )}
      </PopoverContent>
    </Popover>
  );
}
